import { Box } from '@mui/system';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { useEffect, useState } from 'react';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { AutogiroMandateLog as Log } from '../models/autogiroMandateResponse';

type AutogiroMandateLogProps = {
  log: Log[];
};

export const AutogiroMandateLog = (props: AutogiroMandateLogProps) => {
  const { log } = props;
  const { t } = useTranslation();
  const { scrollRef } = useScrollIntoView();

  const [rows] = useState<Log[]>(
    log.map((item) => {
      return { ...item, id: item.autogiroMandateLog_id };
    }),
  );
  const [data, setData] = useState<DataGridModel>({
    loading: false,
    totalRows: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    pageSize: 100,
    page: 0,
  });

  const [sortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    updateData('totalRows', log.length);
  }, [log.length]);

  const columns: GridColDef[] = [
    {
      field: 'message',
      headerName: t('invoice:autogiroMandateStatus.message'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.message}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:autogiroMandateStatus.date'),
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        '& .MuiAccordionSummary-root': {
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
          },
          '& .MuiAccordionSummary-content': {
            ml: 1,
          },
        },
        '& .MuiAccordion-root.Mui-expanded': {
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
          margin: 0,
        },
        '& .MuiAccordion-root': {
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 10px 5px -10px rgba(0, 0, 0, 0.1) inset !important',
          margin: 0,
        },
      }}
    >
      <Accordion disableGutters elevation={0}>
        <AccordionSummary
          expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
          id='autogiroMandateLog-header'
          aria-controls='autogiroMandateLog-content'
        >
          <Typography sx={{ fontWeight: 500 }}>{t('invoice:autogiroMandateStatus.autogiroMandateLog')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              position: 'relative',
              '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                py: 1,
              },
            }}
            ref={scrollRef}
          >
            <DataGrid<Log>
              hidePagination
              columns={columns}
              rows={rows}
              autoHeight
              dataModel={data}
              disablePointer
              filterInToolbar={false}
              columnVisibilityModel={{
                autogiroMandate_id: false,
              }}
              checkboxSelection={false}
              onPageChange={() => ConsoleHelper.log()}
              onPageSizeChange={() => ConsoleHelper.log()}
              sortModel={sortModel}
              onSortModelChange={() => ConsoleHelper.log()}
              onRowClick={() => ConsoleHelper.log()}
              selectionModel={[]}
              onSelectionModelChange={() => ConsoleHelper.log('')}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
